import React from 'react'
import { TextInput, SingleSelect, MultiSelect } from '../forms'
import { AgentWorkflowSection } from './agent-workflow-section'
import { IChecklistItem, ChecklistItemType, ChecklistOwner, FundReleaseType } from '@oneethos/shared'

interface ChecklistItemFormProps {
  newItem: Partial<IChecklistItem>
  setNewItem: (item: Partial<IChecklistItem>) => void
  editingItem: IChecklistItem | null
  saving: boolean
  isLoanFundingPhase: boolean
  onSave: () => void
  onEdit: () => void
  onCancel: () => void
}

const ChecklistItemForm = ({
  newItem,
  setNewItem,
  editingItem,
  saving,
  isLoanFundingPhase,
  onSave,
  onEdit,
  onCancel
}: ChecklistItemFormProps) => {
  return (
    <div className="card-body">
      <div className="row">
        <div className="col-md-6 form-group mb-3">
          <label>Label</label>
          <TextInput
            value={newItem.label || ''}
            onChange={label => setNewItem({ ...newItem, label })}
            placeholder="Checklist item label"
          />
        </div>
        <div className="col-md-6 form-group mb-3">
          <label>Data Field</label>
          <TextInput
            value={newItem.dataField || ''}
            onChange={dataField => setNewItem({ ...newItem, dataField })}
            placeholder="Optional data field reference"
          />
        </div>
      </div>
      <div className='row'>
        <div className="col-md-6 form-group mb-3">
          <label>Description</label>
          <textarea
            className="form-control"
            value={newItem.description || ''}
            onChange={e => setNewItem({ ...newItem, description: e.target.value })}
            rows={3}
            placeholder="Description of the checklist item"
          />
        </div>
        <div className="col-md-6 form-group mb-3">
          <label>Sequence</label>
          <input
            value={newItem.sequence?.toString() || ''}
            type="number"
            step="1.1"
            min="1"
            onChange={e => setNewItem({ ...newItem, sequence: parseFloat(e.target.value) || null })}
            className="form-control"
            placeholder="Sequence of the checklist item"
            required
          />
        </div>
        <div className="col-md-6 form-group mb-3 text-capitalize">
          <label>Active</label>
          <select
            className="form-select"
            value={newItem.active?.toString() || 'true'}
            onChange={e => setNewItem({ ...newItem, active: e.target.value === 'true' })}
          >
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>
      </div>
      <div className='row'>
        <div className='col-md-6 form-group mb-3'>
          <div className="col-md-6 form-group mb-3 text-capitalize">
            <label>owner </label>
            <SingleSelect
              options={Object.values(ChecklistOwner)}
              value={newItem.owner}
              onChange={owner => setNewItem({ ...newItem, owner: owner as ChecklistOwner })}
            />
          </div>
        </div>
        <div className="col-md-6 form-group mb-3 text-capitalize">
          <label>Type </label>
          <SingleSelect
            options={Object.values(ChecklistItemType)}
            value={newItem.type}
            onChange={type => setNewItem({ ...newItem, type: type as ChecklistItemType })}
          />
        </div>
      </div>
      <div className='row'>
        {isLoanFundingPhase && (
          <div className="col-md-6 form-group mb-3 text-capitalize">
            <label>Fund Release Types</label>
            <MultiSelect
              options={Object.fromEntries(
                Object.values(FundReleaseType).map(v => [v, v])
              )}
              values={(newItem.fundReleaseTypes as string[]) || []}
              onChange={(vals) => setNewItem({ ...newItem, fundReleaseTypes: vals as FundReleaseType[] })}
            />
          </div>
        )}
      </div>
      {newItem.type === ChecklistItemType.DocumentUpload && (
        <AgentWorkflowSection
          isSignedContract={newItem.label === 'Signed Contract'}
          agentWorkflowId={newItem.agentWorkflowId}
          presubmitAgentWorkflowId={newItem.presubmitAgentWorkflowId}
          agentWorkflowEnabled={newItem.agentWorkflowEnabled || false}
          onAgentWorkflowIdChange={(agentWorkflowId) => setNewItem({ ...newItem, agentWorkflowId })}
          onPresubmitAgentWorkflowIdChange={(presubmitAgentWorkflowId) =>
            setNewItem({ ...newItem, presubmitAgentWorkflowId })
          }
          onAgentWorkflowEnabledChange={(agentWorkflowEnabled) => setNewItem({ ...newItem, agentWorkflowEnabled })}
        />
      )}
      <div className="d-flex gap-2 justify-content-end">
        <button
          className="btn btn-primary"
          onClick={editingItem ? onEdit : onSave}
          disabled={saving || !newItem.label || !newItem.description || !newItem.sequence}
        >
          {editingItem ? 'Update' : 'Save'}
        </button>
        <button
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

export default ChecklistItemForm